import React, { useRef, useState } from 'react';
import { Toast } from 'primereact/toast';
import { Button } from 'primereact/button';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import TrainingRequestTable from './TrainingRequestTable';
import TrainingRequestForm from './TrainingRequestForm';
import useTrainingRequestData from '../hooks/useTrainingRequestData';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { useLmsPermission } from '../../hooks/useLmsPermission';
import { LMS } from '../../../../constants/permissionCodes';
import useLmsCodeOptions from '../../hooks/useLmsCodeOptions';

// "Talep Edilen Eğitimler" sekmesi — bayinin henüz planlanmamış/yayınlanmamış bir eğitim için
// serbestçe talep oluşturduğu akış (requestType = 2, bkz. TrainingRequestForm.jsx üstündeki not).
const REQUEST_TYPE = 2;

/**
 * "Talep Edilen Eğitimler" sekmesinin içeriği — eskiden `index.jsx`'in tamamı buydu, iki sekmeli
 * yapıya geçişte (`docs/LMS-Egitim-Talebi-Yayinlanan-Egitimler-Roadmap.md`) kendi dosyasına taşındı.
 */
const RequestedTrainingsTab = () => {
    const { th } = useLanguage();
    const { can } = useLmsPermission();
    const [selectedItem, setSelectedItem] = useState(null);
    const [dialogVisible, setDialogVisible] = useState(false);
    const toast = useRef(null);

    const { trainingRequestApi, isLoading, lazyParams, onPage, saveRequest, activeFilters, onFiltersChange, initFilters, sortField, sortOrder, onSortChange, deleteRequest, refetch } = useTrainingRequestData(REQUEST_TYPE);

    const { options: vehicleModelOptions } = useLmsCodeOptions('VEHICLE_MODEL');
    const { options: trainingTypeOptions } = useLmsCodeOptions('TRAINING_TYPES');
    const { options: trainingLocationOptions } = useLmsCodeOptions('TRAINING_LOCATION');

    const handleEdit = (rowData) => {
        setSelectedItem(rowData);
        setDialogVisible(true);
    };

    const handleCreateClick = () => {
        setSelectedItem(null);
        setDialogVisible(true);
    };

    const handleHide = () => {
        setDialogVisible(false);
        setSelectedItem(null);
    };

    const handleSubmit = async (formData) => {
        const success = await saveRequest(formData);
        if (success) {
            handleHide();
            refetch?.();
        }
    };

    const handleDelete = (id) => {
        if (!id) return;
        confirmDialog({
            message: th('trainingRequest.confirmDeleteMessage'),
            header: th('common.delete'),
            icon: 'pi pi-exclamation-triangle',
            acceptClassName: 'p-button-danger',
            accept: async () => {
                // Dialog (ve onun içindeki GetById sorgusu) silme isteğinden ÖNCE kapatılıyor:
                // aksi halde delete mutation'ın `invalidatesTags` tetiklediği otomatik refetch,
                // hâlâ açık/abone olan aynı kaydın GetById sorgusunu (artık silinmiş olduğu için
                // hataya düşecek şekilde) yeniden tetikliyordu.
                handleHide();
                try {
                    await deleteRequest(id);
                    toast.current?.show({ severity: 'success', summary: th('common.success'), detail: th('trainingRequest.deleteSuccess'), life: 3000 });
                    refetch?.();
                } catch {
                    toast.current?.show({ severity: 'error', summary: th('common.error'), detail: th('trainingRequest.deleteError'), life: 4000 });
                }
            }
        });
    };

    return (
        <div>
            <Toast ref={toast} />
            <ConfirmDialog />

            <div className="flex justify-content-end align-items-center mb-4 gap-2">
                <Button label={th('filter.reset')} icon="pi pi-filter-slash" outlined onClick={initFilters} />
                {can(LMS.REQUEST_CREATE) && <Button label={th('trainingRequest.newButton')} icon="pi pi-plus" onClick={handleCreateClick} />}
            </div>

            <TrainingRequestTable
                data={trainingRequestApi}
                totalCount={trainingRequestApi?.totalCount}
                loading={isLoading}
                lazyParams={lazyParams}
                onPage={onPage}
                onEdit={handleEdit}
                activeFilters={activeFilters}
                onFiltersChange={onFiltersChange}
                vehicleModelOptions={vehicleModelOptions}
                trainingTypeOptions={trainingTypeOptions}
                trainingLocationOptions={trainingLocationOptions}
                sortField={sortField}
                sortOrder={sortOrder}
                onSortChange={onSortChange}
            />

            {dialogVisible && <TrainingRequestForm visible={dialogVisible} onHide={handleHide} onSubmit={handleSubmit} initialData={selectedItem} loading={isLoading} handleDelete={can(LMS.REQUEST_DELETE) ? handleDelete : undefined} readOnly={!!selectedItem && !can(LMS.REQUEST_UPDATE)} />}
        </div>
    );
};

export default RequestedTrainingsTab;
